import { Card } from '../Models/Card'
import { getCurrentCardState, getDraggedCards, undo } from './state'
import { getAllCards } from './gameRules'
import { assignCardCoordinates } from './assignCardCoordinates'

const EXPECTED_CARD_COUNT = 52

/**
 * When true, all integrity validation is bypassed.
 * E2e tests set this via `window.disableCardIntegrity()` after seeding
 * partial/synthetic states so that subsequent game actions (drag, double-tap,
 * etc.) don't trigger false-positive corruption reverts.
 */
let _integrityDisabled = false

/** Disable integrity checking for the remainder of this session (test use only). */
export function disableCardIntegrity(): void {
    _integrityDisabled = true
}

/** Re-enable integrity checking. */
function enableCardIntegrity(): void {
    _integrityDisabled = false
}

/**
 * Build a unique key for a card based on its suit type and numeric value.
 * Each (type, numValue) pair is unique in a standard 52-card deck.
 */
function cardKey(card: Card): string {
    return `${card.type}${card.numValue}`
}

/**
 * Validate that all 52 cards are present and accounted for.
 *
 * Collects cards from the current state (stock, waste, tableau, foundation)
 * plus any cards currently being dragged. Checks:
 *   1. Total count equals 52
 *   2. No duplicate (type, numValue) pairs
 *   3. No missing cards
 *
 * On failure, logs detailed diagnostics and auto-reverts to the previous
 * state via undo().
 *
 * Validation is skipped when:
 *  - `_integrityDisabled` is true (set by tests that seed partial states)
 *  - `skipValidation` parameter is true (one-off bypass for a single call)
 *
 * @param skipValidation - When true, skip all integrity checks for this call.
 * @returns true if integrity check passes, false if corruption was detected
 *          and the state was reverted
 */
export function validateCardIntegrity(skipValidation = false): boolean {
    if (skipValidation || _integrityDisabled) return true

    const state = getCurrentCardState()
    if (!state) return true // No state yet (pre-deal), nothing to validate

    const stateCards = getAllCards(state)
    const dragged = getDraggedCards()
    const allCards = [...stateCards, ...dragged]

    const totalCount = allCards.length

    // Build a map of card keys → count
    const seen = new Map<string, number>()
    for (const card of allCards) {
        const key = cardKey(card)
        seen.set(key, (seen.get(key) ?? 0) + 1)
    }

    // Check for missing and duplicate cards
    const suits = ['S', 'C', 'D', 'H']
    const missing: string[] = []
    const duplicates: string[] = []

    for (const suit of suits) {
        for (let num = 1; num <= 13; num++) {
            const key = `${suit}${num}`
            const count = seen.get(key) ?? 0
            if (count === 0) {
                missing.push(key)
            } else if (count > 1) {
                duplicates.push(`${key} (×${count})`)
            }
        }
    }

    if (totalCount === EXPECTED_CARD_COUNT && missing.length === 0 && duplicates.length === 0) {
        return true
    }

    // ─── Integrity violation detected ───────────────────────────────

    console.error(
        `[CardIntegrity] VIOLATION DETECTED — expected ${EXPECTED_CARD_COUNT} cards, found ${totalCount}`,
        {
            stockCount: state.stock.length,
            wasteCount: state.waste.length,
            tableauCounts: state.tableau.map((pile) => pile.length),
            foundationCounts: state.foundation.map((pile) => pile.length),
            draggedCount: dragged.length,
            missing: missing.length > 0 ? missing : '(none)',
            duplicates: duplicates.length > 0 ? duplicates : '(none)',
        }
    )

    // Auto-revert to the previous state
    undo()

    // Re-assign coordinates after undo so the reverted state renders correctly
    const reverted = getCurrentCardState()
    if (reverted) {
        assignCardCoordinates(reverted)
    }

    return false
}
